export type ClosedMonthRow = {
  closingMonth: number;
  closingYear: number;
};

/** GraphQL may return a single closed month or an array. */
export function asClosedMonthRows(
  raw: readonly ClosedMonthRow[] | ClosedMonthRow | null | undefined
): readonly ClosedMonthRow[] {
  if (raw == null) {
    return [];
  }

  if (!Array.isArray(raw)) {
    return [raw as ClosedMonthRow];
  }

  return raw;
}

export type AmendmentIssueDateRuleResult = {
  defaultDate: Date;
  minDate: Date;
  maxDate: Date;
  isClosedMonth: boolean;
};

function day(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const x = day(d);
  x.setDate(x.getDate() + n);

  return x;
}

function clamp(d: Date, min: Date, max: Date): Date {
  const t = day(d).getTime();
  const lo = day(min).getTime();
  const hi = day(max).getTime();

  if (t < lo) {
    return day(min);
  }

  if (t > hi) {
    return day(max);
  }

  return day(d);
}

function addMonths(d: Date, months: number): Date {
  const x = day(d);
  x.setMonth(x.getMonth() + months);

  return x;
}

export type AmendmentEffectiveDateBounds = {
  minDate: Date;
  maxDate: Date;
};

export function getAmendmentEffectiveDateBounds(
  policyEffectiveDate?: string | null,
  policyExpiryDate?: string | null
): AmendmentEffectiveDateBounds | null {
  if (!policyEffectiveDate) {
    return null;
  }

  const minDate = day(new Date(policyEffectiveDate));
  const maxDate = policyExpiryDate
    ? day(new Date(policyExpiryDate))
    : addMonths(minDate, 6);

  return { minDate, maxDate };
}

export function getAmendmentIssueDateRules(
  closedMonths: readonly ClosedMonthRow[] | undefined,
  systemReferenceDate: Date,
  today: Date,
  lineRefDate?: Date,
): AmendmentIssueDateRuleResult {
  const a = day(today);
  const b = day(systemReferenceDate);
  const ref = a.getTime() >= b.getTime() ? a : b;
  const refCal = day(ref);
  const sysCal = day(systemReferenceDate);

  // ClosedMonth.ClosingMonth is 1–12; compare to MonthOf(Max(Today(), SystemReferenceDate)).
  const refMonth = refCal.getMonth() + 1;
  const refYear = refCal.getFullYear();

  const closed =
    !!closedMonths?.length &&
    closedMonths.some(
      (c) =>
        Number(c.closingMonth) === refMonth &&
        Number(c.closingYear) === refYear
    );

  if (closed) {
    const eomonthPlusOne = new Date(refCal.getFullYear(), refCal.getMonth() + 1, 1);
    const closeLineRef = lineRefDate ? day(lineRefDate) : eomonthPlusOne;
    const rangeStart = closeLineRef.getTime() > eomonthPlusOne.getTime() ? closeLineRef : eomonthPlusOne;
    const rangeEnd = addDays(rangeStart, 7);

    return {
      defaultDate: rangeStart,
      minDate: rangeStart,
      maxDate: rangeEnd,
      isClosedMonth: true
    };
  }

  const eomonthMinusPlusOne = new Date(refCal.getFullYear(), refCal.getMonth(), 1);
  const lineRef = lineRefDate ? day(lineRefDate) : eomonthMinusPlusOne;
  const rangeStart = lineRef.getTime() > eomonthMinusPlusOne.getTime() ? lineRef : eomonthMinusPlusOne;
  const maxRef = lineRef.getTime() >= sysCal.getTime() ? lineRef : sysCal;
  const rangeEnd = addDays(maxRef, 7);

  return {
    defaultDate: clamp(refCal, rangeStart, rangeEnd),
    minDate: rangeStart,
    maxDate: rangeEnd,
    isClosedMonth: false
  };
}

export function dateToFormValue(d: Date): string {
  const x = day(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const dd = String(x.getDate()).padStart(2, '0');

  return `${y}-${m}-${dd}`;
}

export function isWithinIssueDateLov(
  value: Date,
  min: Date,
  max: Date
): boolean {
  const t = (x: Date) => day(x).getTime();
  const v = t(value);

  return v >= t(min) && v <= t(max);
}
